import React, { useState, useEffect, useCallback } from 'react';
import { Network, Plus, Trash2, ArrowRight } from 'lucide-react';

const CABLE_COLORS = [
    { label: 'Green', value: '#10b981' },
    { label: 'Blue', value: '#3b82f6' },
    { label: 'Red', value: '#ef4444' },
    { label: 'Yellow', value: '#eab308' },
    { label: 'Purple', value: '#a855f7' },
    { label: 'White', value: '#f8fafc' },
    { label: 'Black', value: '#0f172a' },
    { label: 'Orange', value: '#f97316' },
];

const CableManager = ({ rackId, devices, authenticatedFetch = fetch }) => {
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form State
    const [sourceDeviceId, setSourceDeviceId] = useState('');
    const [sourcePort, setSourcePort] = useState('');
    const [targetDeviceId, setTargetDeviceId] = useState('');
    const [targetPort, setTargetPort] = useState('');
    const [cableColor, setCableColor] = useState(CABLE_COLORS[0].value);
    const [label, setLabel] = useState('');
    const [editingId, setEditingId] = useState(null);

    const fetchConnections = useCallback(async () => {
        try {
            const res = await authenticatedFetch(`/api/network/connections?rackId=${rackId}`);
            if (res.ok) {
                const data = await res.json();
                setConnections(data);
            }
        } catch (err) {
            console.error('Failed to fetch connections:', err);
        } finally {
            setLoading(false);
        }
    }, [authenticatedFetch, rackId]);

    useEffect(() => {
        if (rackId) fetchConnections();
    }, [fetchConnections, rackId]);

    const handleAddConnection = async (e) => {
        e.preventDefault();
        if (!sourceDeviceId || targetDeviceId === '' || targetPort === '' || sourcePort === '') {
            alert('Please select both source and target devices and ports.');
            return;
        }
        if (sourceDeviceId === targetDeviceId && sourcePort === targetPort) {
            alert('Cannot connect a port to itself.');
            return;
        }

        try {
            const url = editingId ? `/api/network/connections/${editingId}` : '/api/network/connections';
            const method = editingId ? 'PUT' : 'POST';

            const res = await authenticatedFetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sourceDeviceId,
                    sourcePort: parseInt(sourcePort),
                    targetDeviceId,
                    targetPort: parseInt(targetPort),
                    cableColor,
                    label
                })
            });
            if (res.ok) {
                await fetchConnections();
                resetForm();
            }
        } catch (err) {
            console.error('Failed to save connection:', err);
        }
    };

    const resetForm = () => {
        setSourceDeviceId('');
        setSourcePort('');
        setTargetDeviceId('');
        setTargetPort('');
        setCableColor(CABLE_COLORS[0].value);
        setLabel('');
        setEditingId(null);
    };

    const handleEditClick = (conn) => {
        setSourceDeviceId(conn.source_device_id?.toString() || '');
        setSourcePort(conn.source_port?.toString() || '');
        setTargetDeviceId(conn.target_device_id?.toString() || '');
        setTargetPort(conn.target_port?.toString() || '');
        setCableColor(conn.cable_color || CABLE_COLORS[0].value);
        setLabel(conn.label || '');
        setEditingId(conn.id);
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Remove this connection?')) return;
        try {
            const res = await authenticatedFetch(`/api/network/connections/${id}`, { method: 'DELETE' });
            if (res.ok) {
                setConnections(prev => prev.filter(c => c.id !== id));
            }
        } catch (err) {
            console.error('Failed to delete connection:', err);
        }
    };

    // Helper to get all configured ports for a device
    const getDevicePorts = (deviceId) => {
        const device = devices.find(d => d.id === parseInt(deviceId));
        if (!device || !device.specs || !device.specs.portBanks) return [];
        
        let totalPorts = 0;
        device.specs.portBanks.forEach(bank => {
            totalPorts += (bank.count || 0);
        });
        
        // Return an array of port indices (0 to totalPorts - 1)
        return Array.from({ length: totalPorts }).map((_, i) => i);
    };

    const getDeviceName = (id) => {
        const d = devices.find(d => d.id === id);
        return d ? (d.model_name || d.name || d.type) : 'Unknown Device';
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                    <Network className="text-blue-400" size={20} />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-white tracking-tight">Cable Manager</h2>
                    <p className="text-white/40 text-xs">Map physical connections between ports</p>
                </div>
            </div>

            {/* Add/Edit Connection Form */}
            <div className={`border rounded-xl p-4 mb-6 transition-all ${editingId ? 'bg-blue-500/[0.05] border-blue-500/30' : 'bg-white/[0.02] border-white/[0.06]'}`}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className={`text-[10px] font-bold uppercase tracking-widest ${editingId ? 'text-blue-400' : 'text-white/40'}`}>
                        {editingId ? 'Edit Connection' : 'New Connection'}
                    </h3>
                    {editingId && (
                        <button type="button" onClick={resetForm} className="text-[10px] text-white/40 hover:text-white uppercase tracking-wider">Cancel</button>
                    )}
                </div>
                <form onSubmit={handleAddConnection} className="space-y-4">
                    
                    {/* Source */}
                    <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-2">
                            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Source Device</label>
                            <select
                                value={sourceDeviceId}
                                onChange={e => { setSourceDeviceId(e.target.value); setSourcePort(''); }}
                                className="w-full bg-[#0a0a0a] border border-white/10 text-white/80 text-sm px-3 py-2 rounded-lg focus:border-white/20 focus:outline-none"
                            >
                                <option value="">Select Device...</option>
                                {devices.map(d => (
                                    <option key={d.id} value={d.id}>U{d.position_u} - {d.model_name || d.type}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Port</label>
                            <select
                                value={sourcePort}
                                onChange={e => setSourcePort(e.target.value)}
                                disabled={!sourceDeviceId}
                                className="w-full bg-[#0a0a0a] border border-white/10 text-white/80 text-sm px-3 py-2 rounded-lg focus:border-white/20 focus:outline-none disabled:opacity-50"
                            >
                                <option value="">--</option>
                                {getDevicePorts(sourceDeviceId).map(p => (
                                    <option key={p} value={p}>Port {p + 1}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Target */}
                    <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-2">
                            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Target Device</label>
                            <select
                                value={targetDeviceId}
                                onChange={e => { setTargetDeviceId(e.target.value); setTargetPort(''); }}
                                className="w-full bg-[#0a0a0a] border border-white/10 text-white/80 text-sm px-3 py-2 rounded-lg focus:border-white/20 focus:outline-none"
                            >
                                <option value="">Select Device...</option>
                                {devices.map(d => (
                                    <option key={d.id} value={d.id}>U{d.position_u} - {d.model_name || d.type}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Port</label>
                            <select
                                value={targetPort}
                                onChange={e => setTargetPort(e.target.value)}
                                disabled={!targetDeviceId}
                                className="w-full bg-[#0a0a0a] border border-white/10 text-white/80 text-sm px-3 py-2 rounded-lg focus:border-white/20 focus:outline-none disabled:opacity-50"
                            >
                                <option value="">--</option>
                                {getDevicePorts(targetDeviceId).map(p => (
                                    <option key={p} value={p}>Port {p + 1}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Options */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Cable Color</label>
                            <div className="flex gap-1.5 flex-wrap">
                                {CABLE_COLORS.map(c => (
                                    <button
                                        type="button"
                                        key={c.value}
                                        title={c.label}
                                        onClick={() => setCableColor(c.value)}
                                        className={`w-6 h-6 rounded-full border-2 transition-all ${cableColor === c.value ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-50 hover:opacity-100'}`}
                                        style={{ backgroundColor: c.value }}
                                    />
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Label (Optional)</label>
                            <input
                                type="text"
                                value={label}
                                onChange={e => setLabel(e.target.value)}
                                placeholder="e.g. Uplink"
                                className="w-full bg-[#0a0a0a] border border-white/10 text-white/80 text-sm px-3 py-2 rounded-lg focus:border-white/20 focus:outline-none"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={!sourceDeviceId || !targetDeviceId || sourcePort === '' || targetPort === ''}
                        className="w-full mt-2 py-2.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-xl font-semibold text-xs uppercase tracking-widest transition-all border border-blue-500/20 disabled:opacity-50 flex justify-center items-center gap-2"
                    >
                        <Plus size={14} className={editingId ? 'hidden' : ''} /> 
                        {editingId ? 'Save Changes' : 'Connect Ports'}
                    </button>
                </form>
            </div>

            {/* Connection List */}
            <div className="flex-1 overflow-y-auto pr-2 space-y-2">
                <h3 className="text-[10px] font-bold text-white/40 uppercase tracking-widest sticky top-0 bg-[#0a0a0a] py-2">Existing Connections ({connections.length})</h3>
                
                {loading ? (
                    <div className="text-center text-white/30 text-xs py-4">Loading...</div>
                ) : connections.length === 0 ? (
                    <div className="text-center text-white/20 text-xs py-8 border border-dashed border-white/10 rounded-xl">
                        No cables connected yet.
                    </div>
                ) : (
                    connections.map(conn => (
                        <div key={conn.id} 
                            onClick={() => handleEditClick(conn)}
                            className={`relative group border p-3 rounded-xl transition-all cursor-pointer hover:bg-white/[0.04] ${editingId === conn.id ? 'bg-white/[0.06] border-white/[0.15] pl-4' : 'bg-white/[0.02] border-white/[0.04]'}`}
                        >
                            <div 
                                className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl opacity-80"
                                style={{ backgroundColor: conn.cable_color || '#10b981' }}
                            />
                            <div className="pl-3 flex justify-between items-start">
                                <div className="space-y-1 w-full">
                                    <div className="flex items-center gap-2 justify-between">
                                        <div className="flex items-center gap-2 text-white/80 text-xs">
                                            <span className="font-semibold truncate max-w-[100px]" title={getDeviceName(conn.source_device_id)}>{getDeviceName(conn.source_device_id)}</span>
                                            <span className="text-white/40 font-mono text-[9px] bg-white/5 px-1 rounded">P{conn.source_port + 1}</span>
                                        </div>
                                        <ArrowRight size={10} className="text-white/20 flex-shrink-0" />
                                        <div className="flex items-center gap-2 text-white/80 text-xs">
                                            <span className="text-white/40 font-mono text-[9px] bg-white/5 px-1 rounded">P{conn.target_port + 1}</span>
                                            <span className="font-semibold truncate max-w-[100px]" title={getDeviceName(conn.target_device_id)}>{getDeviceName(conn.target_device_id)}</span>
                                        </div>
                                    </div>
                                    {conn.label && (
                                        <div className="text-[9px] text-white/40 font-mono tracking-wider">{conn.label}</div>
                                    )}
                                </div>
                                <button 
                                    onClick={() => handleDelete(conn.id)}
                                    className="ml-3 p-1.5 text-red-400/0 group-hover:text-red-400/60 hover:!text-red-400 bg-red-500/0 hover:bg-red-500/10 rounded border border-transparent hover:border-red-500/20 transition-all flex-shrink-0"
                                    title="Remove Connection"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default CableManager;
